import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, GripVertical, Edit2, Check, X, Sparkles } from 'lucide-react';
import { RSVPQuestion } from '@/services/professionalTemplateService';
import { cn } from '@/lib/utils';

interface RSVPQuestionBuilderProps {
  questions: RSVPQuestion[];
  onQuestionsChange: (questions: RSVPQuestion[]) => void;
}

const QUESTION_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'select', label: 'Single Choice' },
  { value: 'multiselect', label: 'Multiple Choice' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' }
];

const QUESTION_CATEGORIES = [
  'professional', 'dining', 'logistics', 'preferences', 'networking', 
  'materials', 'demo', 'communication', 'assessment', 'learning', 
  'certification', 'support', 'special'
];

export const RSVPQuestionBuilder: React.FC<RSVPQuestionBuilderProps> = ({
  questions,
  onQuestionsChange
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const addQuestion = () => {
    const newQuestion: RSVPQuestion = {
      id: `question_${Date.now()}`,
      question: '',
      type: 'text',
      required: false,
      category: 'preferences'
    };
    
    onQuestionsChange([...questions, newQuestion]);
    setEditingIndex(questions.length);
  };

  const updateQuestion = (index: number, updates: Partial<RSVPQuestion>) => {
    const updatedQuestions = questions.map((q, i) => 
      i === index ? { ...q, ...updates } : q
    );
    onQuestionsChange(updatedQuestions);
  };

  const removeQuestion = (index: number) => {
    const updatedQuestions = questions.filter((_, i) => i !== index);
    onQuestionsChange(updatedQuestions);
    setEditingIndex(null);
  };

  const moveQuestion = (fromIndex: number, toIndex: number) => {
    const updatedQuestions = [...questions];
    const [movedQuestion] = updatedQuestions.splice(fromIndex, 1);
    updatedQuestions.splice(toIndex, 0, movedQuestion);
    onQuestionsChange(updatedQuestions);
  };

  const QuestionEditor = ({ question, index }: { question: RSVPQuestion; index: number }) => {
    const isEditing = editingIndex === index;
    
    return (
      <Card className={cn(
        "mb-4 group transition-all duration-200 hover:shadow-md border",
        isEditing && "ring-2 ring-primary/20 shadow-lg"
      )}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="cursor-grab hover:cursor-grabbing touch-none opacity-50 group-hover:opacity-100 transition-opacity">
                <GripVertical className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <CardTitle className="text-base font-semibold">Question {index + 1}</CardTitle>
                  <Badge variant="secondary" className="text-xs font-medium">
                    {QUESTION_TYPES.find(t => t.value === question.type)?.label}
                  </Badge>
                  {question.required && (
                    <Badge className="text-xs bg-gradient-to-r from-destructive to-destructive/80">
                      Required
                    </Badge>
                  )}
                </div>
                {!isEditing && (
                  <p className="text-sm text-muted-foreground truncate">
                    {question.question || 'Untitled Question'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              <Button
                variant={isEditing ? "default" : "ghost"}
                size="sm"
                onClick={() => setEditingIndex(isEditing ? null : index)}
                className="transition-all"
              >
                {isEditing ? (
                  <>
                    <Check className="w-4 h-4 mr-1.5" />
                    Done
                  </>
                ) : (
                  <>
                    <Edit2 className="w-4 h-4 mr-1.5" />
                    Edit
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeQuestion(index)}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        
        {isEditing ? (
          <CardContent className="space-y-5 pt-4 border-t bg-muted/30">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Question Text</Label>
              <Input
                value={question.question}
                onChange={(e) => updateQuestion(index, { question: e.target.value })}
                placeholder="Enter your question..."
                className="bg-background"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Question Type</Label>
                <Select
                  value={question.type}
                  onValueChange={(value: any) => updateQuestion(index, { type: value })}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUESTION_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Category</Label>
                <Select
                  value={question.category}
                  onValueChange={(value) => updateQuestion(index, { category: value })}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUESTION_CATEGORIES.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {(question.type === 'select' || question.type === 'multiselect') && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Answer Options (one per line)</Label>
                <Textarea
                  value={question.options?.join('\n') || ''}
                  onChange={(e) => updateQuestion(index, { 
                    options: e.target.value.split('\n').filter(opt => opt.trim()) 
                  })}
                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                  rows={4}
                  className="bg-background font-mono text-sm"
                />
              </div>
            )}
            
            <div className="flex items-center gap-3 p-4 rounded-lg bg-background border">
              <Checkbox
                id={`required-${index}`}
                checked={question.required}
                onCheckedChange={(checked) => updateQuestion(index, { required: !!checked })}
              />
              <Label htmlFor={`required-${index}`} className="cursor-pointer font-medium">
                Mark as required question
              </Label>
            </div>
          </CardContent>
        ) : (
          <CardContent className="pt-0">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant="outline" className="font-normal">
                {question.category}
              </Badge>
              {question.options && (
                <span className="text-xs text-muted-foreground">
                  {question.options.length} options available
                </span>
              )}
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b">
        <div>
          <h3 className="text-2xl font-bold bg-gradient-to-r from-primary to-primary-light bg-clip-text text-transparent flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            RSVP Question Builder
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Create custom questions to gather specific information from your guests
          </p>
        </div>
        <Button onClick={addQuestion} size="default" className="bg-gradient-to-r from-primary to-primary-light hover:shadow-lg transition-all">
          <Plus className="w-4 h-4 mr-2" />
          Add Question
        </Button>
      </div>
      
      {questions.length === 0 ? (
        <Card className="border-dashed border-2 bg-gradient-to-br from-muted/30 to-muted/10">
          <CardContent className="text-center py-16">
            <div className="max-w-md mx-auto space-y-6">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary/20 to-primary-light/20 flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-primary" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold">No Questions Yet</h3>
                <p className="text-muted-foreground">
                  Start building your RSVP form by adding custom questions tailored to your event needs.
                </p>
              </div>
              <Button onClick={addQuestion} size="lg" className="bg-gradient-to-r from-primary to-primary-light hover:shadow-lg transition-all">
                <Plus className="w-5 h-5 mr-2" />
                Create Your First Question
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm text-muted-foreground px-1">
            <span>{questions.length} question{questions.length !== 1 ? 's' : ''} configured</span>
            <span className="flex items-center gap-1">
              <GripVertical className="w-4 h-4" />
              Drag to reorder
            </span>
          </div>
          {questions.map((question, index) => (
            <QuestionEditor
              key={question.id}
              question={question}
              index={index}
            />
          ))}
        </div>
      )}
    </div>
  );
};